import React, {useCallback, useMemo, useState} from 'react';
import {Box, Menu, MenuItem, Tooltip, useMediaQuery, useTheme} from '@mui/material';
import MoreVert from '@mui/icons-material/MoreVert';
import {useTranslation} from 'react-i18next';
import Button from '../base/button/button';
import IconButton from '../base/button/icon_button';
import type {PageActionItem, PageActionsProps} from './types';

type Presentation = 'buttons' | 'icons' | 'menu';

/**
 * Maps action button colors to paper-* IconButton palette colors used in the panel.
 * @param {string | undefined} color
 * @returns {string}
 */
const resolvePaperIconColor = (color?: string): string => {
    if (!color) {
        return 'paper.primary';
    }
    if (color.startsWith('paper.')) {
        return color;
    }
    if (['primary', 'secondary', 'tertiary', 'error'].includes(color)) {
        return `paper.${color}`;
    }
    return color;
};

/**
 * Responsive page actions for the right side of the title row.
 * Default: md+ buttons → sm icons → xs actions menu.
 */
const PageActions = ({
                         items = [],
                         mode = 'responsive',
                         breakpoints: bp = {button: 'md', icon: 'sm', menu: 'xs'},
                     }: PageActionsProps): React.ReactElement | null => {
    const {t} = useTranslation('components', {keyPrefix: 'page-shell'});
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const visibleItems = useMemo(() => items.filter((item) => !item.hidden), [items]);

    const isDownSm = useMediaQuery(theme.breakpoints.down('sm'));
    const isDownMd = useMediaQuery(theme.breakpoints.down(bp.button ?? 'md'));

    /**
     * Resolves presentation mode for the action list.
     */
    const presentation = useMemo((): Presentation => {
        if (mode === 'always-buttons') return 'buttons';
        if (mode === 'always-icons') return 'icons';
        if (mode === 'always-menu') return 'menu';
        if (isDownSm) return 'menu';
        if (isDownMd) return 'icons';
        return 'buttons';
    }, [isDownMd, isDownSm, mode]);

    /**
     * Opens the overflow actions menu.
     */
    const openMenu = useCallback((e: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(e.currentTarget);
    }, []);

    /**
     * Closes the overflow actions menu.
     */
    const closeMenu = useCallback(() => {
        setAnchorEl(null);
    }, []);

    /**
     * Runs an item click then closes the menu when applicable.
     */
    const handleItemClick = useCallback((item: PageActionItem) => (e: React.MouseEvent<HTMLElement>) => {
        item.onClick?.(e);
        closeMenu();
    }, [closeMenu]);

    if (!visibleItems.length) {
        return null;
    }

    const pinned = visibleItems.filter((item) => item.collapseAt === false);
    const collapsible = visibleItems.filter((item) => item.collapseAt !== false);

    if (presentation === 'buttons') {
        return (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end'}}>
                {visibleItems.map((item) => {
                    const {color, size, ...restButtonProps} = item.buttonProps ?? {};
                    return (
                        <Button
                            key={item.id}
                            variant="outlined"
                            color={(color as 'primary' | 'secondary' | 'tertiary' | 'error' | undefined) ?? 'primary'}
                            size={(size as 'small' | 'medium' | 'large' | undefined) ?? 'medium'}
                            startIcon={item.icon}
                            onClick={item.onClick}
                            {...restButtonProps}
                        >
                            {item.label}
                        </Button>
                    );
                })}
            </Box>
        );
    }

    if (presentation === 'icons') {
        return (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0}}>
                {visibleItems.map((item) => {
                    const {color, size, ...restButtonProps} = item.buttonProps ?? {};
                    return (
                        <Tooltip key={item.id} title={item.label}>
                            <IconButton
                                color={resolvePaperIconColor(typeof color === 'string' ? color : undefined) as any}
                                size={(size as 'small' | 'medium' | 'large' | undefined) ?? 'medium'}
                                onPressed={item.onClick}
                                aria-label={item.label}
                                {...restButtonProps}
                            >
                                {item.icon ?? item.label?.[0]}
                            </IconButton>
                        </Tooltip>
                    );
                })}
            </Box>
        );
    }

    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0}}>
            {pinned.map((item) => {
                const {color, size, ...restButtonProps} = item.buttonProps ?? {};
                return (
                    <Button
                        key={item.id}
                        variant="outlined"
                        color={(color as 'primary' | 'secondary' | 'tertiary' | 'error' | undefined) ?? 'primary'}
                        size={(size as 'small' | 'medium' | 'large' | undefined) ?? 'medium'}
                        startIcon={item.icon}
                        onClick={item.onClick}
                        {...restButtonProps}
                    >
                        {item.label}
                    </Button>
                );
            })}
            {collapsible.length > 0 && (
                <>
                    <Tooltip title={t((keys) => keys.actionsMenu)}>
                        <IconButton
                            color="paper.secondary"
                            size="medium"
                            onPressed={openMenu}
                            aria-label={t((keys) => keys.actionsMenu)}
                            aria-haspopup="true"
                            aria-expanded={Boolean(anchorEl)}
                        >
                            <MoreVert/>
                        </IconButton>
                    </Tooltip>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={closeMenu}
                        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                        transformOrigin={{vertical: 'top', horizontal: 'right'}}
                    >
                        {collapsible.map((item) => (
                            <MenuItem key={item.id} onClick={handleItemClick(item)}>
                                {item.icon && (
                                    <Box component="span" sx={{display: 'inline-flex', mr: 1, alignItems: 'center'}}>
                                        {item.icon}
                                    </Box>
                                )}
                                {item.label}
                            </MenuItem>
                        ))}
                    </Menu>
                </>
            )}
        </Box>
    );
};

export default PageActions;
